import React, { useState } from "react";
import { mockData } from "../data/mockData";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Heart, Activity, Users, Shield, RefreshCw, Star } from "lucide-react";

const iconMap = {
  Heart,
  Activity,
  Users,
  Shield,
  RefreshCw,
  Star
};

const ServicesSection = ({ data }) => {
  const services = data?.services || [];
  const paymentOptions = data?.paymentOptions || [];
  const testimonials = data?.testimonials || [];
  const [activeService, setActiveService] = useState(null);

  return (
    <section id="services" className="py-24 bg-black">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-light mb-8">What we do</h2>
            <div className="w-24 h-px bg-red-500 mx-auto mb-8"></div>
            <p className="text-xl text-white/80 max-w-3xl mx-auto font-light">
              Comprehensive healthcare services designed specifically for retirement village residents
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className={`bg-gray-900/50 border-white/10 hover:border-red-500/30 transition-all duration-500 cursor-pointer group ${
                    activeService === service.id ? 'border-red-500/50 scale-105' : ''
                  }`}
                  onMouseEnter={() => setActiveService(service.id)}
                  onMouseLeave={() => setActiveService(null)}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-red-500/10 rounded-lg group-hover:bg-red-500/20 transition-all duration-300">
                        <IconComponent className="w-6 h-6 text-red-500" />
                      </div>
                      <Badge variant="outline" className="border-white/20 text-white/60 group-hover:border-red-500/30 group-hover:text-red-500/80">
                        0{service.id}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-light text-white group-hover:text-red-100 transition-colors duration-300">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white/70 mb-6 leading-relaxed font-light">
                      {service.description}
                    </p>
                    <div className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center">
                          <div className="w-1 h-1 bg-red-500 rounded-full mr-3"></div>
                          <span className="text-sm text-white/60 font-light">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Payment Options */}
          <div className="mb-16">
            <h3 className="text-3xl font-light text-center mb-12">Payment Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paymentOptions.map((option, index) => (
                <Card 
                  key={index}
                  className="bg-gray-900/30 border-white/10 hover:border-red-500/30 transition-all duration-300 hover:scale-105 group"
                >
                  <CardContent className="p-6">
                    <h4 className="text-lg font-light text-white mb-3 group-hover:text-red-100 transition-colors duration-300">
                      {option.title}
                    </h4>
                    <p className="text-white/70 text-sm leading-relaxed font-light">
                      {option.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Service-Specific Testimonials */}
          <div className="mb-16">
            <h3 className="text-2xl font-light text-center mb-8">Success Stories by Service</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.slice(0, 4).map((testimonial) => (
                <div key={testimonial.id} className="bg-gray-800/30 rounded-lg p-6 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center group-hover:bg-orange-500/30 transition-all duration-300">
                        <span className="text-orange-500 font-bold text-lg">{testimonial.name.charAt(0)}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex text-orange-500 mb-2 text-sm">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <span key={i}>★</span>
                        ))}
                      </div>
                      <p className="text-white/80 italic text-sm leading-relaxed font-light mb-3">
                        "{testimonial.quote}"
                      </p>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-white font-light text-sm">{testimonial.name}{testimonial.age ? `, ${testimonial.age}` : ''}</p>
                        </div>
                        <div className="text-xs bg-red-500/20 text-red-500 px-2 py-1 rounded">
                          {testimonial.programme}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <p className="text-lg text-white/80 mb-8 font-light">
              Residents are also welcome to visit us as private clients.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-sm font-light transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-red-500/25"
                onClick={() => {
                  const element = document.querySelector("#contact");
                  if (element) element.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Start Your Journey
              </button>
              <button 
                className="border border-white/30 text-white hover:bg-white hover:text-black px-8 py-3 rounded-sm font-light transition-all duration-300 hover:scale-105"
                onClick={() => {
                  const element = document.querySelector("#work");
                  if (element) element.scrollIntoView({ behavior: "smooth" });
                }}
              >
                See Our Impact
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;